import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import Logo from '../assets/images/logo.png'
import { AiOutlineMenu } from 'react-icons/ai'
import { IoMdClose } from 'react-icons/io'
import { FiHeart, FiShoppingCart, FiUser } from 'react-icons/fi'
import '../assets/css/Header.css'
import { ASSET_TYPES, fetchAssetsByType } from '../api/assetApi'
import { useCart } from '../context/CartContext'
import { useWishlist } from '../context/WishlistContext'
import {
  getShopAuthLabel,
  isAdminAuthenticated,
  logoutAdmin,
} from '../admin/utils/auth'

const Header = () => {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [servicesList, setServicesList] = useState([])
  const [authTick, setAuthTick] = useState(0)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)
  const handleClose = () => {
    setExpanded(false)
    setUserMenuOpen(false)
  }
  const { cartCount } = useCart()
  const { wishlistCount } = useWishlist()

  const loggedIn = isAdminAuthenticated()
  const accountLabel = getShopAuthLabel() || 'Account'

  useEffect(() => {
    const sync = () => setAuthTick((n) => n + 1)
    window.addEventListener('auth-changed', sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener('auth-changed', sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  useEffect(() => {
    if (!userMenuOpen) return undefined
    const onPointerDown = (event) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [userMenuOpen])

  useEffect(() => {
    let cancelled = false
    fetchAssetsByType(ASSET_TYPES.SERVICES)
      .then((items) => {
        if (!cancelled) setServicesList(items)
      })
      .catch(() => {
        if (!cancelled) setServicesList([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  // authTick forces re-render when login/logout fires
  void authTick

  const wishlistBadge =
    wishlistCount > 99
      ? '99+'
      : wishlistCount > 0
        ? String(wishlistCount)
        : null
  const cartBadge =
    cartCount > 99 ? '99+' : cartCount > 0 ? String(cartCount) : null

  const handleLogout = () => {
    logoutAdmin()
    handleClose()
    navigate('/')
  }

  const userMenu = (
    <div
      className="header-user-menu"
      ref={userMenuRef}
      onMouseEnter={() => setUserMenuOpen(true)}
      onMouseLeave={() => setUserMenuOpen(false)}
    >
      <button
        type="button"
        className={`header-icon-btn${userMenuOpen ? ' active' : ''}`}
        aria-label={`Account menu for ${accountLabel}`}
        aria-expanded={userMenuOpen}
        onClick={() => setUserMenuOpen((open) => !open)}
      >
        <FiUser />
      </button>
      {userMenuOpen ? (
        <div className="header-user-dropdown">
          <p className="header-user-dropdown__name text-lora">{accountLabel}</p>
          <NavLink
            to="/order-history"
            className="header-user-dropdown__link"
            onClick={handleClose}
          >
            Order History
          </NavLink>
          <button
            type="button"
            className="header-user-dropdown__action"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      ) : null}
    </div>
  )

  return (
    <>
      <header>
        <nav
          className={`navbar navbar-expand-md navbar-light bg-white shadow-sm  main-navbar`}
          id="mainNavbar"
        >
          <div className="container">
            <div className="brand">
              <a className="navbar-brand d-flex align-items-center" href="/">
                <img src={Logo} alt="Logo" className="me-4 site-logo" />
              </a>
            </div>
            <div className="d-flex align-items-center gap-2 ms-auto me-2 d-md-none">
              {loggedIn ? userMenu : null}
              <NavLink
                to="/wishlist"
                className="header-icon-btn header-icon-btn--badge"
                aria-label="Wishlist"
                onClick={handleClose}
              >
                <FiHeart />
                {wishlistBadge && (
                  <span className="header-badge">{wishlistBadge}</span>
                )}
              </NavLink>
              <NavLink
                to="/cart"
                className="header-icon-btn header-icon-btn--badge"
                aria-label="Cart"
                onClick={handleClose}
              >
                <FiShoppingCart />
                {cartBadge && <span className="header-badge">{cartBadge}</span>}
              </NavLink>
            </div>
            <button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarNav"
              aria-controls="navbarNav"
              aria-expanded={expanded ? 'true' : 'false'}
              aria-label="Toggle navigation"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <IoMdClose /> : <AiOutlineMenu />}
            </button>
            <div
              className={`collapse navbar-collapse pb-3 pb-lg-0 ${
                expanded ? 'show' : ''
              }`}
              id="navbarNav"
            >
              <ul className="navbar-nav mx-auto">
                <li>
                  <NavLink onClick={handleClose} to="/" className="nav-link ">
                    Home
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink
                    onClick={handleClose}
                    to="/about"
                    className="nav-link "
                  >
                    About
                  </NavLink>
                </li>
                <li className="nav-item relative  service-link ">
                  <NavLink
                    to="/services"
                    className="nav-link dropdown-toggle"
                    onClick={handleClose}
                  >
                    Service
                  </NavLink>

                  <div className="service-dropdown dropdown">
                    <ul className="service-inner-card">
                      {servicesList.map((service) => {
                        return (
                          <li key={service.id}>
                            <NavLink to="/services" onClick={handleClose}>
                              {service.title}
                            </NavLink>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                </li>
                <li className="nav-item">
                  <NavLink
                    onClick={handleClose}
                    to="/shop"
                    className="nav-link"
                  >
                    Shop
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink
                    onClick={handleClose}
                    to="/training-classes"
                    className="nav-link"
                  >
                    Training Classes
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink
                    onClick={handleClose}
                    to="/news-articles"
                    className="nav-link "
                  >
                    News & Article
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink
                    onClick={handleClose}
                    to="/contact"
                    className="nav-link "
                  >
                    Contact Us
                  </NavLink>
                </li>
              </ul>

              <div className="d-flex align-items-center gap-3 mb-lg-0 mb-3 header-actions">
                {loggedIn ? (
                  <div className="d-none d-md-block">{userMenu}</div>
                ) : (
                  <NavLink
                    to="/login"
                    className="login-btn rounded-md text-lora"
                    onClick={handleClose}
                  >
                    Login
                  </NavLink>
                )}
                <NavLink
                  to="/wishlist"
                  className="header-icon-btn header-icon-btn--badge d-none d-md-inline-flex"
                  aria-label="Wishlist"
                  onClick={handleClose}
                >
                  <FiHeart />
                  {wishlistBadge && (
                    <span className="header-badge">{wishlistBadge}</span>
                  )}
                </NavLink>
                <NavLink
                  to="/cart"
                  className="header-icon-btn header-icon-btn--badge d-none d-md-inline-flex"
                  aria-label="Cart"
                  onClick={handleClose}
                >
                  <FiShoppingCart />
                  {cartBadge && (
                    <span className="header-badge">{cartBadge}</span>
                  )}
                </NavLink>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  )
}

export default Header
